import { computed } from 'vue';
import { useRoute } from 'vue-router';
import moment from 'moment';
import { useAcl, useI18n } from '@/composables';
import { useStore } from '@/store';

export type ReportFormWorkspaceContext = {
  edit?: boolean;
  mode?: string;
};

export const createReportFormWorkspace = (
  getContext: () => ReportFormWorkspaceContext = () => ({}),
) => {
  const store = useStore();
  const { selectOnlyPrivateWorkspaces, aclCanCreateOnSection } = useAcl();

  const workspacesList = computed(() => {
    const { edit, mode } = getContext();

    if (mode === 'edit' || edit === true) {
      return store.user.workspaces;
    }

    const workspaces = selectOnlyPrivateWorkspaces.value
      ? store.allPrivateWorkspaces
      : store.user.workspaces;

    return workspaces.filter((workspace: { id: string }) =>
      aclCanCreateOnSection({ workspaceId: workspace.id }),
    );
  });

  const defaultWorkspace = computed(() => {
    const selectedWorkspaceIds = store.user.selectedWorkspaces;
    const list = workspacesList.value;

    return (
      (selectedWorkspaceIds.length
        && list.find((workspace: { id: string }) => selectedWorkspaceIds.includes(workspace.id)))
      || list.find((workspace: { name: string }) => workspace.name === 'Master')
      || list[0]
    );
  });

  return {
    workspacesList,
    defaultWorkspace,
  };
};

export const useReportForm = (
  getContext: () => ReportFormWorkspaceContext = () => ({}),
) => {
  const store = useStore();
  const route = useRoute();
  const { t } = useI18n();
  const { workspacesList, defaultWorkspace } = createReportFormWorkspace(getContext);

  const isMainTab = computed(() =>
    ['campaigns', 'offers', 'landings', 'flows', 'traffic-sources', 'affiliate-networks'].includes(
      route.params.type as string,
    ),
  );

  const rotationOptions = computed(() => [
    {
      key: 'random',
      value: t('Random Rotation'),
    },
    {
      key: 'paths',
      value: t('Paths Rotation'),
      planLimits: 'smartRotations',
    },
    {
      key: 'landings',
      value: t('Paths & Landings Rotation'),
      planLimits: 'smartRotations',
    },
    {
      key: 'landingsAndOffers',
      value: t('Paths & Landings & Offers Rotation'),
      planLimits: 'smartRotations',
    },
    {
      key: 'fixOnPath',
      value: t('Fix on Paths'),
      planLimits: 'smartRotations',
    },
    {
      key: 'fixOnLanding',
      value: t('Fix on Paths & Landings'),
      planLimits: 'smartRotations',
    },
    {
      key: 'fixOnOffers',
      value: t('Fix on Paths & Landings & Offers'),
      planLimits: 'smartRotations',
    },
  ]);

  const buildUpdateInfo = (form: { updated?: string; created?: string; updatedBy?: string }) => {
    const { updated, created } = form;
    let { updatedBy } = form;

    if (!updatedBy) updatedBy = t('No Name');

    const date = moment(updated || created).format('DD MMM YYYY');
    const name = updatedBy.length > 47 ? `${updatedBy.substring(0, 45)}...` : updatedBy;

    return t('<b>Last Updated:</b> {0} <div>{1}</div>', [`&nbsp;<span class="name">${name}</span>`, date]);
  };

  const loadForm = (params: {
    defaultForm: () => Record<string, unknown>;
    loadAdditionalData?: (args: unknown[]) => Promise<unknown>;
    form: Record<string, unknown>;
    workspaceId?: string;
  }) => {
    const workspaceId = params.workspaceId || defaultWorkspace.value.id;

    Object.assign(params.form, {
      ...params.defaultForm(),
      workspaceId,
    });

    if (params.loadAdditionalData) {
      return params.loadAdditionalData([workspaceId]);
    }

    return Promise.resolve(null);
  };

  const onChangeWorkspace = (params: {
    value: string;
    defaultForm: () => Record<string, unknown>;
    loadAdditionalData?: (args: unknown[]) => Promise<unknown>;
    form: Record<string, unknown>;
    preserveName?: string;
  }) => {
    const loadPromise = params.loadAdditionalData
      ? params.loadAdditionalData([params.value])
      : Promise.resolve(null);

    return loadPromise.then(() => {
      Object.assign(params.form, {
        ...params.defaultForm(),
        workspaceId: params.value,
        name: params.preserveName ?? params.form.name,
      });
    });
  };

  return {
    store,
    workspacesList,
    defaultWorkspace,
    isMainTab,
    rotationOptions,
    buildUpdateInfo,
    loadForm,
    onChangeWorkspace,
  };
};
